import { Avatar, Row, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Button from 'components/Button';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import Text from 'components/Text';
import ModalCashDeposit, { useModalCashDepositRef } from 'features/cashier/components/CashDeposit/ModalCashDeposit';
import { tableGreenStrStyle } from 'features/report/components/Component';
import { ReportType } from 'features/report/pages/ReportPage/constants';
import reportActions from 'features/report/services/actions';
import reportSelectors from 'features/report/services/selectors';
import { ICashDepositReportItem } from 'features/report/services/types/cash-deposit';
import { IParamItemType, ITimeClockReportHistory } from 'features/report/services/types/reportStaff';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';
import { useAppDispatch } from 'store/hooks';
import { styled } from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';

interface ICashDepositTable {
    data?: ITimeClockReportHistory[],
    disabelEdit?: boolean;
}
interface DataType extends ICashDepositReportItem {
}
const v2 = true;
const CashDepositTable: React.FC<ICashDepositTable> = ({ disabelEdit }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const dataCashDepositReport = reportSelectors.getDataCashDepositReport();
    const params = reportSelectors.getParams()?.[ReportType.CASH_DEPOSIT];
    const modalCashDepositRef = useModalCashDepositRef();
    const modalConfirmRef = useModalConfirmRef();

    const columns: ColumnsType<DataType> = [
        {
            title: 'STAFF',
            dataIndex: 'staffName',
            key: '1',
            render: (_, record) => (
                <NameBox>
                    <ImageContainer>
                        <Avatar
                            size={'large'}
                            style={{ zoom: 1.5 }}
                            src={record.avatar}
                        >{record?.staffName?.charAt(0)}</Avatar>
                    </ImageContainer>

                    <Text variant="CONTENT_1" color="text_3">
                        {record.staffName}
                    </Text>
                </NameBox>
            ),
            width: '15%',
        },
        {
            title: 'DATE',
            dataIndex: 'date',
            key: '2',
            width: '15%',
            render: (value) => <Text variant="CONTENT_1" color="text_3">{value || '--/--/--'}</Text>,
        },
        {
            title: 'TIME',
            dataIndex: 'time',
            key: '3',
            width: '15%',
            render: (strClockInTime) => (
                <Text variant="CONTENT_1" color="text_3">{strClockInTime}</Text>),
        },
        {
            title: 'AMOUNT',
            dataIndex: 'amount',
            key: '4',
            width: '15%',
            render: (value) => (
                <Text variant="CONTENT_1" color="text_3">{formatCurrency(value)}</Text>),
        },
        {
            title: 'NOTE',
            dataIndex: 'note',
            key: '5',
            render: (value) => (
                <Text variant="CONTENT_1" color="text_3">{value}</Text>),
        },
        {
            title: 'ACTIONS',
            width: '250px',
            fixed: 'right',
            render: (_, record: DataType) => {
                if (disabelEdit) return null;
                return (
                    <Row style={{ gap: '12px', justifyContent: 'center' }}>
                        <Button ntype="LIGHT_BLUE" onClick={() => modalCashDepositRef.current?.detail(record.id, record.note)} icon='show' style={{ width: 60, height: 50 }} />
                        <Button
                            ntype="SECONDARY"
                            onClick={() => {
                                const pathURL = `/store/${storage.shop_id.get()}/report/print/cash-deposit-preview?cashDepositId=${record.id}`;
                                navigate(pathURL);
                            }}
                            icon='print'
                            style={{ width: 60, height: 50 }}
                        />
                        <Button ntype="SECONDARY" onClick={() => {
                            modalConfirmRef.current?.open('Do you like to remove this cash deposit?', () => {
                                dispatch(reportActions.deleteCashDeposit(record.id));
                            });
                        }} icon='deleteIcon' style={{ width: 60, height: 50 }} />
                    </Row>
                );
            },
        }
    ];

    return (
        <>
            <StyledCashDepositTable className={v2 ? 'v2' : ''}>
                <Table
                    columns={columns}
                    dataSource={dataCashDepositReport?.content || []}
                    style={v2 ? { width: '100%' } : { width: '100%', padding: '1rem' }}
                    rowClassName={'row-table'}
                    pagination={{ pageSize: 20, current: params?.page || 1 }}
                    onChange={(pagination) => {
                        dispatch(reportActions.setParams({
                            [ReportType.CASH_DEPOSIT]: {
                                ...params || {},
                                page: pagination.current || 1,
                            } as IParamItemType,
                        }));
                    }}
                    scroll={{ y: '70vh' }}
                />
            </StyledCashDepositTable>
            <ModalCashDeposit detailMode ref={modalCashDepositRef} />
            <ModalConfirm ref={modalConfirmRef} />
        </>
    );
};
export default CashDepositTable;

const StyledCashDepositTable = styled.div`
    flex: 1;
    border-radius: 5px;
    border: 1px solid #86909C;
    background: #FFF;

    /* box */
    box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.10) inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    ${tableGreenStrStyle}

&.v2 {
  flex: 1;
  box-shadow: none;
  border: none;
  .ant-table-wrapper {
    padding: 0;
  }
  .ant-table-wrapper .ant-table {
    .ant-table-thead th.ant-table-cell {
      height: 48px;
      padding: 0px 8px;
      gap: 10px;
      align-self: stretch;
      border-bottom: 1px solid #CCD4DC;
      background: #6FABB6;
      color: #fff;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      text-transform: capitalize;
      text-align: left !important;
      &::before {
        width: 1.5px !important;
        height: 100% !important;
      }
    }

    .ant-table-tbody td.ant-table-cell {
      .BODY_1, .CONTENT_1 {
        color: #1D2129;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }

    .ant-table-tbody .ant-table-row:nth-child(even) td {
      background: #E4F4F6;
      border-bottom: 1px solid #E4F4F6;
    }
  }
}
`;

const ImageContainer = styled.div`
  min-width: 40px;
  margin-right: 0.5rem;
`;

const NameBox = styled(Row)`
  align-items: center;
  flex-wrap: nowrap;
  text-align: start;
`;
